import { Building } from './building'
import { Road } from './road'
import { Resource, Purse } from './resources'
import { PQueue, PQueueRecord } from './pqueue'

function building_pair_id(lhs: number, rhs: number): number {
    if (lhs < rhs) {
        return lhs * Building.max_id + rhs
    } else {
        return rhs * Building.max_id + lhs
    }
}

export enum Priority {
    // doesn't want item
    None,

    // stocking, not a priority at all
    Stock,

    // uses the item
    Use,

    // built out of the item
    Construction,
}

const descending_priorities: Priority[] = [
    Priority.Construction,
    Priority.Use,
    Priority.Stock,
    // NB None deliberately missing as it should never be a part of the calcs
]

class Graph {
    private building_ids: Set<number> = new Set()

    // If this contains the mapping we know they are connected
    private edges: Set<number> = new Set()
    
    update_from(buildings: Building[], roads: Road[]): void {
        this.building_ids.clear()
        this.edges.clear()

        for (let b of buildings) {
            this.building_ids.add(b.id)
        }

        for (let r of roads) {
            this.edges.add(building_pair_id(r.source.id, r.destination.id))
        }
    }

    public shortest_distance(start: Building, finish: Building): number | null {
        let visited = new Set<number>()

        let pq = new PQueue<number>()
        pq.push({ 
            distance: 0,
            data: start.id
        })

        while (true) {
            const rec = pq.pop()
            if (rec == null) {
                return null
            }

            if (rec.data == finish.id) {
                return rec.distance
            }

            this.building_ids.forEach(next_building_id => {
                if (!this.edges.has(building_pair_id(rec.data, next_building_id))) {
                    return
                }

                if (visited.has(next_building_id)) {
                    return
                }
                visited.add(next_building_id)

                pq.push({
                    // TODO should this be road length?
                    distance: rec.distance + 1,
                    data: next_building_id,
                })
            })
        }
    }

    // This should return true if a unit going start -> finish should take a link to via
    public should_route_via(start: Building, finish: Building, via: Building): boolean {
        const shortest = this.shortest_distance(start, finish)
        if (shortest == null) {
            return false
        }

        const from_next = this.shortest_distance(via, finish)
        if (from_next == null) {
            return false
        }
        
        return (from_next + 1) <= shortest
    }
}

export class Distributor {
    private graph: Graph = new Graph()
    public buildings: Building[] = []
    public roads: Road[] = []

    public update_from(buildings: Building[], roads: Road[]): void {
        this.buildings = buildings
        this.roads = roads
        this.graph.update_from(buildings, roads)
    }

    private requesters(available: Resource, priority: Priority): Building[] {
        let r: Building[] = []

        for (let b of this.buildings) {
            if (b.wants(this.resources_en_route(b), available) == priority) {
                r.push(b)
            }
        }

        return r
    }

    public resources_en_route(destination: Building): Purse {
        let p = new Purse(false)

        for (let b of this.buildings) {
            for (let parcel of b.waiting_parcels) {
                if (parcel.destination.equal_to(destination.position)) {
                    p.add_resource(1, parcel.resource)
                }
            }
        }

        for (let r of this.roads) {
            let cvs = r.visual_state()
            if (cvs.carried != null) {
                if (cvs.carried.destination.equal_to(destination.position)) {
                    p.add_resource(1, cvs.carried.resource)
                }
            }
        }

        return p
    }

    // don't worry about the contents, just calculate a route
    public should_route_parcel(source: Building, destination: Building, next_building: Building): boolean {
        return this.graph.should_route_via(source, destination, next_building)
    }

    public find_journey(resource_options: Resource[], next: Building, source: Building): [Resource, Building] | null {
        for (let priority of descending_priorities) {
            for (let resource of resource_options) {
                for (let req of this.requesters(resource, priority)) {
                    if (this.graph.should_route_via(source, req, next)) {
                        return [resource, req]
                    }
                }
            }
        }

        return null
    }
}

